import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Radio, Tag, ScanLine, ArrowLeft } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';

const RFIDDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const menuItems = [
    {
      id: 'registro',
      title: 'Registro de Prendas',
      description: 'Registra tags RFID asociándolos a prendas y clientes. Lectura individual o por lote con el lector de escritorio USB.',
      icon: <Tag className="h-10 w-10 text-blue-600 dark:text-blue-400" />,
      path: '/rfid/registro',
      iconBg: 'bg-blue-500/15',
      borderColor: 'hover:border-blue-500/50',
      color: 'bg-blue-500/10',
      isReady: true,
    },
    {
      id: 'conteo',
      title: 'Conteo de Prendas',
      description: 'Escaneo masivo con antena RFID de mediano alcance. Cuenta prendas en una caneca y las agrupa por cliente.',
      icon: <ScanLine className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />,
      path: '/rfid/conteo',
      iconBg: 'bg-emerald-500/15',
      borderColor: 'hover:border-emerald-500/50',
      color: 'bg-emerald-500/10',
      isReady: true,
    },
  ];

  const handleMenuClick = (item) => {
    if (!item.isReady) {
      toast({
        title: 'En desarrollo',
        description: 'Esta funcionalidad estará disponible próximamente',
      });
      return;
    }
    navigate(item.path);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="p-4 md:p-6"
    >
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-border/50">
          <div className="flex items-center gap-4">
            <div>
              <Button asChild variant="outline" size="sm">
                <a onClick={() => navigate(-1)} className="cursor-pointer">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Volver
                </a>
              </Button>
            </div>
            <div className="bg-blue-500/15 p-2.5 rounded-lg">
              <Radio className="h-7 w-7 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-2xl">Gestión RFID</CardTitle>
              <CardDescription className="mt-1">
                Registro y conteo de prendas con tecnología RFID
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-8 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {menuItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => handleMenuClick(item)}
                className={`${item.color} p-8 rounded-xl border-2 border-border shadow-sm transition-all duration-200 ${
                  !item.isReady
                    ? 'opacity-60 cursor-not-allowed'
                    : 'cursor-pointer hover:shadow-lg ' + item.borderColor
                }`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className={`p-4 rounded-xl ${item.iconBg}`}>
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-semibold mt-4">{item.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    {item.description}
                  </p>
                  {!item.isReady && (
                    <span className="mt-3 px-3 py-1 text-xs rounded-full bg-muted text-muted-foreground">
                      Próximamente
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default RFIDDashboard;
